import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users } from "lucide-react";

export default function ManagerTeamPage() {
  const [executives, setExecutives] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [e, t] = await Promise.all([api.get("/marketing/my-executives"), api.get("/tasks")]);
      setExecutives(e.data);
      setTasks(t.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const statsFor = (execId) => {
    const own = tasks.filter(t => t.assigned_to_user_id === execId);
    return {
      total: own.length,
      pending: own.filter(t => t.status === "pending").length,
      in_progress: own.filter(t => t.status === "in_progress").length,
      completed: own.filter(t => t.status === "completed").length,
    };
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Marketing · Manager</p>
        <h1 className="font-display text-3xl text-white mt-1">My Team</h1>
      </div>

      <div className="px-10 py-8">
        {loading ? (
          <div className="text-center text-gray-500 py-20">Loading team…</div>
        ) : executives.length === 0 ? (
          <div className="iz-card p-12 text-center">
            <Users className="w-8 h-8 text-[#d4af37] mx-auto mb-3" strokeWidth={1.5}/>
            <p className="text-gray-500 text-sm">No executives report to you yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {executives.map(x => {
              const s = statsFor(x.id);
              const pct = s.total > 0 ? Math.round((s.completed / s.total) * 100) : 0;
              return (
                <div key={x.id} className="iz-card p-5" data-testid={`team-card-${x.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-[#1a1a1a] border border-[#d4af37]/30 flex items-center justify-center text-sm font-semibold text-[#d4af37]">
                      {x.name.split(" ").map(n => n[0]).slice(0,2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm">{x.name}</p>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 truncate">{x.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-[#1a1a1a] text-center">
                    <div>
                      <p className="text-[9px] font-mono uppercase tracking-widest text-gray-500">Pending</p>
                      <p className="font-display text-xl text-white">{s.pending}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase tracking-widest text-gray-500">In Prog.</p>
                      <p className="font-display text-xl text-[#d4af37]">{s.in_progress}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase tracking-widest text-gray-500">Done</p>
                      <p className="font-display text-xl text-[#10b981]">{s.completed}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">
                      <span>Completion</span>
                      <span className="text-[#d4af37]">{pct}%</span>
                    </div>
                    <div className="h-1 bg-[#1a1a1a] rounded-full overflow-hidden">
                      <div className="h-full bg-[#d4af37]" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
